// Syncs unsynced app_log entries from IndexedDB to the server.
//
// Called by field_sync_controller after the main sync cycle completes.
// Logs are a low-priority background task -- failures are silently ignored
// and retried on the next sync cycle.
//
// Entries are batched (up to 100 per request). syncLogs loops bounded
// batches per call (MAX_BATCHES_PER_CALL) rather than sending just one, so a
// large backlog drains over a few sync passes instead of one per cycle
// (Card #3799 -- previously a flood of routine logs plus a slow sync cadence
// could hold evidence back for 20-60h). Each batch also prioritises
// error/warn entries over info/event ones, so the one log that actually
// matters isn't stuck behind a queue of routine noise.
//
// After a successful POST, entries are marked syncedAt so they are not
// re-sent. They remain in IndexedDB for local viewing in the diagnostic page.
//
// One shipment at a time (card #3866). syncLogs runs at the end of several
// sync passes; two overlapping calls used to read the same unsynced rows and
// both POST them, so the server stored every line twice. Now:
//   - calls in the same context share the shipment already in flight;
//   - a Web Lock (LOG_SYNC_LOCK_NAME) keeps other tabs and the service worker
//     out while one ships, and they skip rather than queue behind it;
//   - each batch is claimed (syncingAt) inside one IndexedDB transaction
//     before the request, so any other caller skips those rows, and released
//     on failure for the next pass. A claim older than CLAIM_STALE_MS (a tab
//     closed mid-request) is ignored.
// The server also ignores a line it already holds (Api::V1::Field::LogsController),
// for the one case no client guard covers: a request that landed but whose
// answer never came back.

import db from "./field_db"
import { observedFetch, currentLevel } from "./field_network_health"
import { timeoutFor } from "./field_timeout_policy"

const BATCH_SIZE = 100
const MAX_BATCHES_PER_CALL = 5 // bounded -- a single sync pass never loops forever under a huge backlog
const LOG_URL    = "/api/v1/field/logs"
const LOG_SYNC_LOCK_NAME = "win-field-log-sync"
const CLAIM_STALE_MS = 60_000 // well past log_sync's longest timeout (15s)

let _inFlight = null

export function syncLogs(csrfToken) {
  if (_inFlight) return _inFlight
  _inFlight = _shipUnderLock(csrfToken).finally(() => { _inFlight = null })
  return _inFlight
}

// -- private --

async function _shipUnderLock(csrfToken) {
  if (!navigator.locks?.request) return _ship(csrfToken)
  // ifAvailable: another context already shipping has these rows; skip.
  return navigator.locks.request(LOG_SYNC_LOCK_NAME, { ifAvailable: true }, lock => (lock ? _ship(csrfToken) : 0))
}

async function _ship(csrfToken) {
  let totalSent = 0

  for (let i = 0; i < MAX_BATCHES_PER_CALL; i++) {
    const sent = await _syncBatch(csrfToken)
    if (sent === 0) break
    totalSent += sent
    if (sent < BATCH_SIZE) break // drained
  }

  return totalSent
}

function _isClaimed(row, now) {
  return !!row.syncingAt && now - new Date(row.syncingAt).getTime() < CLAIM_STALE_MS
}

// Selects the next batch to send, prioritising error/warn entries first so
// they ship even when a big backlog of routine info/event logs exists, and
// claims it (syncingAt) in the same transaction so no other caller picks the
// same rows.
async function _claimNextBatch() {
  return db.transaction("rw", db.app_log, async () => {
    const now = Date.now()
    const priority = await db.app_log
      .filter(r => !r.syncedAt && !_isClaimed(r, now) && (r.level === "error" || r.level === "warn"))
      .limit(BATCH_SIZE)
      .toArray()

    const rest = priority.length >= BATCH_SIZE ? [] : await db.app_log
      .filter(r => !r.syncedAt && !_isClaimed(r, now) && r.level !== "error" && r.level !== "warn")
      .limit(BATCH_SIZE - priority.length)
      .toArray()

    const batch = [...priority, ...rest]
    const syncingAt = new Date(now).toISOString()
    await db.app_log.bulkUpdate(batch.map(r => ({ key: r.id, changes: { syncingAt } })))
    return batch
  })
}

async function _releaseClaim(ids) {
  await db.app_log.bulkUpdate(ids.map(id => ({ key: id, changes: { syncingAt: null } }))).catch(() => {})
}

// The running build, from the field layout's app-version meta tag. Lets the
// server page only for phones on the current build (card #3994). Null in a
// context with no document (the service worker).
function _appVersion() {
  if (typeof document === "undefined") return null
  return document.querySelector('meta[name="app-version"]')?.content || null
}

async function _syncBatch(csrfToken) {
  const unsent = await _claimNextBatch()

  if (unsent.length === 0) return 0
  const ids = unsent.map(r => r.id)

  const payload = unsent.map(r => ({
    level:        r.level,
    category:     r.category   || null,
    message:      r.message    || null,
    detail:       r.detail     || null,
    logged_at:    r.timestamp,
    place_id:     r.placeId    || null,
    mission_id:   r.missionId  || null,
    response_id:  r.responseId || null,
    platform_os:  r.platformOs || null,
    display_mode: r.displayMode || null
  }))

  let response
  try {
    response = await observedFetch(LOG_URL, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept":        "application/json",
        "X-CSRF-Token":  csrfToken
      },
      body:   JSON.stringify({ logs: payload, source: "field", app_version: _appVersion() }),
      signal: AbortSignal.timeout(timeoutFor("log_sync", currentLevel()))
    }, { kind: "log", sizeClass: "small" })
  } catch {
    await _releaseClaim(ids)
    return 0 // network failure -- retry next cycle
  }

  if (!response.ok) {
    await _releaseClaim(ids)
    return 0
  }

  // Mark all sent entries as synced
  const now = new Date().toISOString()
  await db.app_log.bulkUpdate(ids.map(id => ({ key: id, changes: { syncedAt: now, syncingAt: null } })))

  return unsent.length
}
